import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  censusServiceConfigs,
  programUnitConfigs,
  censusBillingRules,
} from "../services/soar";
import FloatingInput from "../common/FloatingInput";
import ExpandDiv from "../common/ExpandDiv";

function ServiceConfig(props) {
  const { serviceConfigId } = useParams();
  const [config, setConfig] = useState({});
  const [programUnits, setProgramUnits] = useState([]);
  const [billingRules, setBillingRules] = useState([]);
  const [data, setData] = useState({});
  const navigate = useNavigate();
  const applicationId =
    process.env.REACT_APP_ENVIRONMENT === "Production"
      ? "5dae139e-d052-4bd8-bf4a-499a31ac3c15"
      : "58990f83-8566-4ba8-b4b5-584a64e4cba1";

  const writeAccess = JSON.parse(
    localStorage.getItem("safyInfo")
  ).user.modules.includes("Admin");

  const statuses = [
    "Bed Hold",
    "Day Rsp",
    "Day Dbl Pd Rsp",
    "Dbl Pd Rsp",
    "Discharged",
    "Group",
    "Occupied",
    "Paid Leave",
    "Pass Through",
    "Placement Pending",
    "Respite",
    "UnPd Leave",
    "UnPd Rsp",
    "With Child",
    "Without Child",
  ];

  useEffect(() => {
    getConfig();
    getProgramUnits();
    loadBillingRules();
  }, []);

  const getConfig = async () => {
    const response = await censusServiceConfigs({
      command: "query",
      application: applicationId,
      keyword: "byid",
      ids: [serviceConfigId],
    });
    if (response.code === "success") {
      setConfig(response.data[0]);
    } else {
      toast.error("Error Loading Service Configuration");
    }
  };

  const getProgramUnits = async () => {
    const response = await programUnitConfigs({
      command: "query",
      environment: "SOARQueryLive",
      keyword: "viewall",
    });

    if (response.code === "success") {
      const pus = response.data
        .filter((pu) => pu.statuscode === "Yes")
        .map((pu) => ({
          _id: pu.ccx_unitid,
          name: pu.ccx_name,
          glCode: pu.generalLedger?.census,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      setProgramUnits(pus);
    } else {
      toast.error(`Error in loading Program Units. ${response.message}`);
    }
  };

  const loadBillingRules = async () => {
    const response = await censusBillingRules({
      command: "query",
      application: applicationId,
      keyword: "activerulesforserviceconfiguration",
      searchTerms: {
        serviceConfiguration: serviceConfigId,
      },
    });
    setBillingRules(response.data);
    setData({ ...data, billingRulesLoaded: true });
  };

  const saveConfig = async () => {
    const response = await censusServiceConfigs({
      command: "update",
      application: applicationId,
      id: config.id,
      name: config.name,
      service: config.serviceId,
      configuration: { ...config.configuration, serviceId: config.serviceId },
      effective: config.effective,
      expiration: config.expiration,
    });

    if (response.code === "success") {
      toast.info("Config Saved Successfully");
    } else {
      toast.error(`Error Saving Config ${response.message}`);
    }
  };

  const deactivateService = async () => {
    const response = await censusServiceConfigs({
      application: applicationId,
      command: "deactivate",
      id: config.id,
    });

    if (response.code !== "success") {
      toast.error("Error Deactivating Service Configuration");
    } else {
      toast.success("Service Configuration Deactivated Successfully");
      navigate("/admin/services");
    }
  };

  const addItem = (item, group) => {
    let obj = { ...config };
    const objGroup = obj.configuration[group] ? obj.configuration[group] : [];
    objGroup.push(item);
    obj["configuration"][group] = objGroup;
    setConfig(obj);
  };

  const removeItem = (item, group) => {
    let obj = { ...config };
    let groupConfig = obj.configuration[group].filter((i) => i !== item);
    obj.configuration[group] = groupConfig;

    setConfig(obj);
  };

  const repositionItem = (item, group, direction) => {
    const index = config.configuration[group].indexOf(item);
    if (direction === "up") {
      if (index > 0) {
        config.configuration[group].splice(
          index - 1,
          0,
          config.configuration[group].splice(index, 1)[0]
        );
      }
    } else {
      if (index < config.configuration[group].length - 1) {
        config.configuration[group].splice(
          index + 1,
          0,
          config.configuration[group].splice(index, 1)[0]
        );
      }
    }
    setConfig({ ...config });
  };

  return (
    <div>
      <div className="row">
        <div className="col-11 h4">Service Configuration</div>
        <div className="col-1 pt-2 mt-2">
          {data.billingRulesLoaded && billingRules.length === 0 && (
            <i
              className="fas fa-trash fa-lg text-danger float-end"
              role="button"
              onClick={() => deactivateService()}
              title="Deactivate Service Configuration"
            />
          )}
        </div>
      </div>
      <div className="floating-toolbox">
        <div className="clearfix mb-3">
          <div className="float-start"></div>
          <div className="text-center" onClick={() => saveConfig()}>
            <i
              className="far fa-save fa-2x floating-button floating-save rounded-circle pt-2 text-white"
              style={{ backgroundColor: "#28a745" }}
            />
          </div>
        </div>
      </div>
      <div className="row rounded bg-safy2-1 py-2" title={config.id}>
        <div className="col">
          <FloatingInput
            name="name"
            label="Name"
            value={config.name}
            onChange={(e) => setConfig({ ...config, name: e.target.value })}
            disabled={!writeAccess}
          />
        </div>
        <div className="col">
          <FloatingInput
            type="select"
            name="state"
            label="State"
            options={[
              { _id: "AL", name: "AL" },
              { _id: "CO", name: "CO" },
              { _id: "IN", name: "IN" },
              { _id: "KY", name: "KY" },
              { _id: "NV", name: "NV" },
              { _id: "OH", name: "OH" },
              { _id: "SC", name: "SC" },
            ]}
            value={config.configuration?.state || ""}
            onChange={(e) =>
              setConfig({
                ...config,
                configuration: {
                  ...config.configuration,
                  state: e.target.value,
                },
              })
            }
            disabled={!writeAccess}
          />
        </div>
        <div className="col">
          <FloatingInput
            type="select"
            name="type"
            label="Type"
            options={[
              { _id: "TFC", name: "TFC" },
              { _id: "Residence", name: "Residence" },
            ]}
            value={config?.configuration?.type || ""}
            onChange={(e) => {
              setConfig({
                ...config,
                configuration: {
                  ...config.configuration,
                  type: e.target.value,
                },
              });
            }}
            disabled={!writeAccess}
          />
        </div>
        <div className="col">
          <FloatingInput
            name="effective"
            label="Effective"
            type="date"
            value={config.effective}
            onChange={(e) =>
              setConfig({ ...config, effective: e.target.value })
            }
            disabled={!writeAccess}
          />
        </div>
        <div className="col">
          <FloatingInput
            name="expiration"
            label="Expiration"
            type="date"
            value={config.expiration}
            onChange={(e) =>
              setConfig({ ...config, expiration: e.target.value })
            }
            disabled={!writeAccess}
          />
        </div>
      </div>
      <div className="row">
        <div className="col">
          <div className="rounded border border-dark bg-light mt-4 p-2">
            <div className="h4 ps-2">Program Units</div>
            <div className="row">
              <div className="col-6">
                {writeAccess && (
                  <FloatingInput
                    className="mb-4"
                    type="select"
                    name="programSelect"
                    label="Select Program Unit to Add"
                    options={programUnits.filter(
                      (pu) =>
                        !config.configuration?.programUnits?.find(
                          (p) => p._id === pu._id
                        )
                    )}
                    onChange={(e) => {
                      if (e.target.value !== "") {
                        addItem(
                          programUnits.find((p) => p._id === e.target.value),
                          "programUnits"
                        );
                        e.target.value = "";
                      }
                    }}
                  />
                )}
                {config.configuration?.programUnits?.map((pu, index) => (
                  <div
                    key={`PU-${index}-${pu._id}`}
                    className="rounded border border-dark bg-secondary text-white p-2 mb-2"
                  >
                    {pu.name}
                    {writeAccess && (
                      <i
                        className="fas fa-trash text-warning float-end me-2"
                        role="button"
                        onClick={() => removeItem(pu, "programUnits")}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="col">
          <FloatingInput
            className="border-dark bg-light mt-4"
            name="serviceCode"
            label={<div className="h5 bg-light">Service Code</div>}
            value={config.configuration?.serviceCode || ""}
            onChange={(e) =>
              setConfig({
                ...config,
                configuration: {
                  ...config.configuration,
                  serviceCode: e.target.value,
                },
              })
            }
          />
          <div className="rounded border border-dark bg-light mt-4 p-2">
            <div className="h4 ps-2">Statuses</div>
            {config?.configuration?.serviceStatuses?.length > 0 && (
              <>
                {config?.configuration?.serviceStatuses?.map(
                  (service, index) => (
                    // <div className="btn btn-sm border border-dark m-1 bg-info text-white">
                    //   {service}
                    // </div>
                    <div
                      key={`${service}${index}`}
                      className="d-inline-flex btn btn-sm btn-secondary m-1 p-1"
                    >
                      {writeAccess && index > 0 && (
                        <i
                          className="fas fa-arrow-left text-info m-1"
                          role="button"
                          onClick={() =>
                            repositionItem(service, "serviceStatuses", "up")
                          }
                        />
                      )}
                      <div className="mx-1">
                        {service}
                        {writeAccess && (
                          <i
                            className="fas fa-trash text-warning ps-2"
                            onClick={() =>
                              removeItem(service, "serviceStatuses")
                            }
                          />
                        )}
                      </div>
                      {writeAccess &&
                        index <
                          config.configuration.serviceStatuses.length - 1 && (
                          <i
                            className="fas fa-arrow-right text-info m-1"
                            role="button"
                            onClick={() =>
                              repositionItem(service, "serviceStatuses", "down")
                            }
                          />
                        )}
                    </div>
                  )
                )}
              </>
            )}
            <div className="ps-2 mt-4">
              {writeAccess && (
                <ExpandDiv title="Available Statuses">
                  <div>
                    {statuses
                      .filter(
                        (status) =>
                          !config.configuration?.serviceStatuses?.includes(
                            status
                          )
                      )
                      .map((status) => (
                        <div
                          key={status}
                          className={`btn btn-sm btn-info border border-dark m-1 `}
                          onClick={() => addItem(status, "serviceStatuses")}
                        >
                          {status}
                        </div>
                      ))}
                  </div>
                </ExpandDiv>
              )}
            </div>
          </div>
        </div>
        <div className="rounded border border-dark bg-light mt-4 mx-3 p-2">
          <div className="h4">Billing Rules</div>
          {billingRules
            .filter((br) => !br.expiration || br.expiration === "")
            .map((br) => (
              <div
                key={br._id}
                className="row rounded border border-dark bg-primary text-white p-2 mb-2 mx-2"
              >
                <div className="col">{br.payorName}</div>
                <div className="col">{br.groupPlanName}</div>
                <div className="col">{br.serviceConfigurationName}</div>
                <div className="col">{br.effective}</div>
                <div className="col">
                  <i
                    className="fas fa-circle-arrow-right fa-xl float-end mt-2 pt-1"
                    role="button"
                    onClick={() => navigate(`/rev/rules/${br.id}`)}
                  ></i>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

export default ServiceConfig;
